import { AlertCircle, Cloud, X } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  EntityCombobox,
  type ComboboxOption,
} from "@/components/ui/entity-combobox";
import { CloudMediaManager } from "@/pages/CloudMediaPage";
import { useAwsMediaSummary } from "@/hooks/useAwsMedia";
import {
  useCreateProjectAwsResourceLink,
  useDeleteProjectAwsResourceLink,
  useProjectAwsResourceLinks,
} from "@/hooks/useProjectAwsResourceLinks";
import { useProjectWriteAvailability } from "@/hooks/useProjects";
import {
  awsResourceKey,
  awsResourceTypeLabel,
  awsSummaryCandidates,
  resolveAwsResourceLinks,
  type AwsResourceCandidate,
  type AwsResourceType,
} from "@/lib/aws-resource-links";

const LINKABLE_TYPES: ReadonlySet<AwsResourceType> = new Set([
  "mediaconnect_flow",
  "medialive_channel",
]);

export function ProjectCloudResourcesSection({
  projectId,
}: {
  projectId: string;
}) {
  const links = useProjectAwsResourceLinks(projectId);
  const summary = useAwsMediaSummary();
  const deleteLink = useDeleteProjectAwsResourceLink();
  const createLink = useCreateProjectAwsResourceLink();
  const { canWrite, reason } = useProjectWriteAvailability();
  const resolvedLinks = resolveAwsResourceLinks(links.data, summary.data);

  const candidates = awsSummaryCandidates(summary.data).filter((c) =>
    LINKABLE_TYPES.has(c.type),
  );
  const linkedKeys = new Set(links.data.map((l) => awsResourceKey(l)));
  const unlinkedCandidates = candidates.filter(
    (c) => !linkedKeys.has(awsResourceKey(c)),
  );
  const candidatesByKey = new Map<string, AwsResourceCandidate>(
    unlinkedCandidates.map((c) => [awsResourceKey(c), c]),
  );
  const linkOptions: ComboboxOption[] = unlinkedCandidates.map((c) => ({
    value: awsResourceKey(c),
    label: c.name ?? c.id ?? c.arn ?? "Unnamed",
    subtitle: awsResourceTypeLabel(c.type),
  }));

  // Links rendered as full components by the manager below.
  const managedKeys = new Set(
    links.data
      .filter((l) => LINKABLE_TYPES.has(l.resource_type))
      .map((l) => awsResourceKey(l)),
  );
  // Anything the manager won't render (legacy types or missing from AWS).
  const fallbackLinks = resolvedLinks.filter(
    ({ link, resource }) =>
      !(LINKABLE_TYPES.has(link.resource_type) && resource),
  );

  const handleLink = (key: string | null) => {
    if (!key) return;
    const candidate = candidatesByKey.get(key);
    if (!candidate) return;
    createLink.mutate({
      project_id: projectId,
      resource_type: candidate.type,
      resource_id: candidate.id ?? candidate.arn ?? null,
      resource_arn: candidate.arn ?? null,
      resource_name: candidate.name ?? null,
      metadata: {},
    });
  };

  return (
    <Card data-testid="project-cloud-resources">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle className="flex items-center gap-2">
          <Cloud className="h-5 w-5" />
          Cloud Resources
        </CardTitle>
        {canWrite && (
          <div className="w-64 max-w-full">
            <EntityCombobox
              options={linkOptions}
              value={null}
              onChange={handleLink}
              placeholder="Link a flow or channel…"
              searchPlaceholder="Search flows and channels…"
              emptyMessage="No unlinked flows or channels"
              disabled={createLink.isPending || summary.isLoading}
              allowClear={false}
            />
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {summary.isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>AWS media unavailable</AlertTitle>
            <AlertDescription>
              Saved links are shown without live AWS state.
            </AlertDescription>
          </Alert>
        )}

        {links.isLoading ? (
          <div className="text-sm text-muted-foreground">
            Loading cloud resources...
          </div>
        ) : resolvedLinks.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
            No AWS resources linked to this project.
          </div>
        ) : (
          <>
            {managedKeys.size > 0 && (
              <CloudMediaManager
                projectId={projectId}
                filterKeys={managedKeys}
                layout="stacked"
                showCreate={false}
                showHeader={false}
                emptyMessage="Linked resources are not currently available in AWS."
              />
            )}

            {fallbackLinks.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {fallbackLinks.map(({ link, resource }) => {
                  const displayName =
                    resource?.name ??
                    link.resource_name ??
                    link.resource_arn ??
                    link.resource_id ??
                    "Unnamed resource";
                  return (
                    <Badge
                      key={link.id}
                      variant={resource ? "secondary" : "destructive"}
                      className="gap-1 pr-1 font-normal"
                    >
                      <span className="max-w-48 truncate">
                        {displayName}
                        <span className="ml-1 opacity-70">
                          · {awsResourceTypeLabel(link.resource_type)}
                        </span>
                        {!resource && (
                          <span className="ml-1">· Missing from AWS</span>
                        )}
                      </span>
                      <button
                        type="button"
                        aria-label={`Unlink ${displayName}`}
                        disabled={!canWrite || deleteLink.isPending}
                        title={canWrite ? "Unlink from project" : reason}
                        onClick={() =>
                          deleteLink.mutate({
                            id: link.id,
                            projectId: link.project_id,
                          })
                        }
                        className="rounded-sm hover:bg-muted-foreground/20 disabled:opacity-50"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  );
                })}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
